import React, { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, Chip, CircularProgress, Button } from '@mui/material';
import { useParams, Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import { Navigation, Thumbs } from 'swiper/modules';
import api from '../utils/api';

SwiperCore.use([Navigation, Thumbs]);

const isObjectId = (v) => /^[0-9a-fA-F]{24}$/.test(v || '');

const RoomDetails = () => {
  const { id } = useParams(); 
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        setLoading(true);

        let res;
        if (isObjectId(id)) {
          res = await api.get(`/api/rooms/${id}`);
        } else {
          res = await api.get(`/api/rooms/slug/${id}`);
        }

        const data = res?.data?.data || null;
        if (!data) {
          setError('Room not found');
          setRoom(null);
        } else {
          setRoom(data);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading room:', err);
        setError('Failed to load room details.');
        setRoom(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRoom();
  }, [id]);

  const imageUrls = useMemo(() => {
    const imgs = room?.images || [];
    return (imgs || []).map((img) => img?.url).filter(Boolean);
  }, [room]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !room) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>{error || 'Room not found'}</Typography>
        <Button component={Link} to="/rooms" variant="contained" sx={{ mt: 2 }}>
          Back to Rooms
        </Button>
      </Container>
    );
  }

  const features = room.amenities || [];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {room.name}
        </Typography>

        <Typography variant="h6" color="text.secondary">
          From ${room.basePrice}{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            per night
          </Typography>
        </Typography>

        <Box sx={{ mt: 2 }}>
          {features.map((f, i) => (
            <Chip key={i} label={f} size="small" sx={{ mr: 1, mb: 1 }} />
          ))}
        </Box>
      </Box>

      {imageUrls.length > 0 ? (
        <>
          <Swiper
            modules={[Navigation, Thumbs]}
            navigation
            spaceBetween={10}
            slidesPerView={1}
            style={{ borderRadius: 8, overflow: 'hidden' }}
          >
            {imageUrls.map((src, idx) => (
              <SwiperSlide key={idx}>
                <Box
                  component="img"
                  src={src}
                  alt={`${room.name} ${idx + 1}`}
                  loading="lazy"
                  sx={{ width: '100%', height: { xs: 260, sm: 420, md: 520 }, objectFit: 'cover' }}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {imageUrls.map((src, idx) => (
              <Grid item xs={3} sm={2} md={2} key={`thumb-${idx}`}>
                <Box
                  component="img"
                  src={src}
                  alt={`${room.name} thumb ${idx + 1}`}
                  loading="lazy"
                  sx={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 1 }}
                />
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              No images have been added yet.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default RoomDetails;
